import { useState } from "react";
import { X } from "lucide-react";
import { Cliente } from "../../services/clientes";

interface Props {
  editing: Cliente | null;
  onClose: () => void;
  onSubmit: (dados: any) => void;
  loading: boolean;
  error?: string;
}

export function ClienteFormModal({ editing, onClose, onSubmit, loading, error }: Props) {
  const [nome, setNome] = useState(editing?.nome || "");
  const [email, setEmail] = useState(editing?.email || "");
  const [telefone, setTelefone] = useState(editing?.telefone || "");
  const [documento, setDocumento] = useState(editing?.documento || "");
  const [observacoes, setObservacoes] = useState(editing?.observacoes || "");
  const [linkAssets, setLinkAssets] = useState(editing?.linkAssets || "");
  const [criarUsuario, setCriarUsuario] = useState(false);
  const [senhaInicial, setSenhaInicial] = useState("");
  const [ativo, setAtivo] = useState(editing?.ativo ?? true);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const payload: any = {
      nome,
      email: email || undefined,
      telefone: telefone || undefined,
      documento: documento || undefined,
      observacoes: observacoes || undefined,
      linkAssets: linkAssets.trim() || null,
    };
    if (editing) {
      payload.ativo = ativo;
    } else if (criarUsuario) {
      payload.criarUsuario = true;
      payload.senhaInicial = senhaInicial;
    }
    onSubmit(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">{editing ? "Editar cliente" : "Novo cliente"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="text-xs text-slate-600">Nome / Razão social</label>
            <input type="text" required value={nome} onChange={(e) => setNome(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" autoFocus />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">E-mail <span className="text-slate-400">(opcional)</span></label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Telefone <span className="text-slate-400">(opcional)</span></label>
              <input type="text" value={telefone} onChange={(e) => setTelefone(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600">Documento (CPF/CNPJ) <span className="text-slate-400">(opcional)</span></label>
            <input type="text" value={documento} onChange={(e) => setDocumento(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Pasta de assets do cliente <span className="text-slate-400">(opcional)</span></label>
            <input type="url" value={linkAssets} onChange={(e) => setLinkAssets(e.target.value)} placeholder="https://drive.google.com/…" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
            <p className="text-[11px] text-slate-400 mt-0.5">Link puxado automaticamente nas tarefas como "Assets cliente".</p>
          </div>
          <div>
            <label className="text-xs text-slate-600">Observações internas <span className="text-slate-400">(opcional)</span></label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
          </div>

          {!editing && (
            <div className="border-t border-slate-100 pt-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={criarUsuario} onChange={(e) => setCriarUsuario(e.target.checked)} />
                Criar acesso ao portal do cliente
              </label>
              {criarUsuario && (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-slate-500">Será criado um usuário com perfil "cliente" usando o e-mail acima.</p>
                  <input type="password" required={criarUsuario} minLength={6} placeholder="Senha inicial (mín. 6)" value={senhaInicial} onChange={(e) => setSenhaInicial(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
                </div>
              )}
            </div>
          )}

          {editing && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
              Cliente ativo
            </label>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              {loading ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
